import {CaptionStyleContextMenu} from '@waka/compat/CaptionStyleContextMenu';
import {FrameVideoContextMenu} from '@waka/compat/FrameVideoContextMenu';
import {ImageActionsContextMenu} from '@waka/compat/ImageActionsContextMenu';
import { AudioMergeContextMenu } from '@waka/compat/AudioMergeContextMenu';
import { ClipAudioContextMenu } from '@waka/compat/ClipAudioContextMenu';
import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { MenuItem, toast } from '@crabs-ai/ds-react';
import { copyEditorSelection } from '@waka/compat/clipboard-actions';
import { ContextMenuItem, ContextMenuSeparator } from '../../context-menu';
import {
  FEATURE_BRING_TO_FRONT,
  FEATURE_COPY_LAYERS,
  FEATURE_CUT_LAYERS,
  FEATURE_DUPLICATE_LAYERS,
  FEATURE_SEND_TO_BACK,
} from '../../flags';
import { getAssetStartInSeconds } from '../../assets/utils';
import { EditorStarterItem } from '../../items/item-type';
import { bringToFrontOrBack } from '../../state/actions/bring-item-to-front-or-back';
import { duplicateItems } from '../../state/actions/duplicate-items';
import { removeSilence } from '../../state/actions/remove-silence';
import {
  detectSilenceSegments,
  type SilenceSegment,
} from '../../utils/silence-detection';
import {
  useAllItems,
  useAssets,
  useFps,
  useSelectedItems,
  useTracks,
  useWriteContext,
  useCurrentStateAsRef,
} from '../../utils/use-context';
import { getAssetFromItem } from '../../assets/utils';
import type { RemoveSilenceDialogConfirmParams } from '../remove-silence-dialog';

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.userAgent);
const modKey = isMac ? '⌘' : 'Ctrl+';

const canRemoveSilence = (item: EditorStarterItem) =>
  item.type === 'video' || item.type === 'audio';

export const TimelineItemContextMenu: React.FC<{
  item: EditorStarterItem;
  onRequestRemoveSilenceDialog?: (
    onConfirm: (
      params: RemoveSilenceDialogConfirmParams,
    ) => void | Promise<void>,
  ) => void;
}> = ({ item, onRequestRemoveSilenceDialog }) => {
  const { setState } = useWriteContext();
  const stateRef = useCurrentStateAsRef();
  const { selectedItems } = useSelectedItems();
  const { items: allItems } = useAllItems();
  const { assets } = useAssets();
  const { tracks } = useTracks();
  const { fps } = useFps();
  const t = useTranslations();
  const [isRemovingSilence, setIsRemovingSilence] = useState(false);

  const isLocked = useMemo(() => {
    const track = tracks.find((t) => t.items.includes(item.id));
    return track?.locked ?? false;
  }, [tracks, item.id]);

  const isMultiSelection = useMemo(
    () => selectedItems.length > 1 && selectedItems.includes(item.id),
    [selectedItems, item.id],
  );

  const targetItems = useMemo(() => {
    return isMultiSelection
      ? selectedItems
          .map((id) => allItems[id])
          .filter((item): item is EditorStarterItem => item !== undefined)
      : [item];
  }, [isMultiSelection, selectedItems, item, allItems]);

  const itemsRemovableSilence = useMemo(
    () => targetItems.filter(canRemoveSilence),
    [targetItems],
  );
  const hasAnyRemovableWithUrl = useMemo(
    () =>
      itemsRemovableSilence.some(
        (it) => getAssetFromItem({ item: it, assets })?.remoteUrl,
      ),
    [itemsRemovableSilence, assets],
  );

  const handleBringToFront = useCallback(
    (e: Event) => {
      e.stopPropagation();
      setState({
        update: (state) =>
          bringToFrontOrBack({
            state,
            itemId: item.id,
            position: 'front',
          }),
        commitToUndoStack: true,
      });
    },
    [item.id, setState],
  );

  const handleSendToBack = useCallback(
    (e: Event) => {
      e.stopPropagation();
      setState({
        update: (state) =>
          bringToFrontOrBack({
            state,
            itemId: item.id,
            position: 'back',
          }),
        commitToUndoStack: true,
      });
    },
    [item.id, setState],
  );

  const handleCopy = useCallback(
    (e: Event) => {
      e.stopPropagation();
      void copyEditorSelection({
        stateRef,
        setState,
        ids: targetItems.map((item) => item.id),
      });
    },
    [targetItems, stateRef, setState],
  );

  const handleCut = useCallback(
    (e: Event) => {
      e.stopPropagation();
      void copyEditorSelection({
        stateRef,
        setState,
        ids: targetItems.map((item) => item.id),
        cut: true,
      });
    },
    [targetItems, setState, stateRef],
  );

  const handleDuplicate = useCallback(
    (e: Event) => {
      e.stopPropagation();
      setState({
        update: (state) =>
          duplicateItems(
            state,
            targetItems.map((targetItem) => targetItem.id),
          ),
        commitToUndoStack: true,
      });
    },
    [targetItems, setState],
  );

  const handleConfirmRemoveSilence = useCallback(
    async (params: RemoveSilenceDialogConfirmParams) => {
      const toProcess = itemsRemovableSilence.filter((it) => {
        const a = getAssetFromItem({ item: it, assets });
        return a?.remoteUrl != null;
      });
      if (toProcess.length === 0) {
        return;
      }
      setIsRemovingSilence(true);
      try {
        const results = await Promise.all(
          toProcess.map(async (it) => {
            const a = getAssetFromItem({ item: it, assets });
            const audioUrl = a?.remoteUrl ?? null;
            if (!audioUrl)
              return { itemId: it.id, silenceSegments: [] as SilenceSegment[] };
            const startInSeconds = getAssetStartInSeconds(it) ?? 0;
            const durationInSeconds = it.durationInFrames / fps;
            const silenceSegments = await detectSilenceSegments({
              audioUrl,
              startInSeconds,
              durationInSeconds,
              minSilenceDurationMs: params.minSilenceDurationMs,
              silenceThresholdDb: params.silenceThresholdDb,
            });
            return { itemId: it.id, silenceSegments };
          }),
        );
        const withSilence = results.filter((r) => r.silenceSegments.length > 0);
        if (withSilence.length === 0) {
          toast({
            title: t('Editor.timeline.context-menu.remove-silence'),
            description: t(
              'Editor.timeline.context-menu.remove-silence-no-silence',
            ),
            toastOptions: { position: 'top-center' },
          });
          return;
        }
        const order = toProcess.map((it) => {
          const track = tracks.find((tr) => tr.items.includes(it.id));
          const trackIndex = track ? tracks.indexOf(track) : 0;
          return { itemId: it.id, trackIndex, from: it.from };
        });
        const sorted = withSilence
          .map((r) => ({
            itemId: r.itemId,
            silenceSegments: r.silenceSegments,
            orderKey: order.find((o) => o.itemId === r.itemId),
          }))
          .sort(
            (a, b) =>
              (a.orderKey?.trackIndex ?? 0) - (b.orderKey?.trackIndex ?? 0) ||
              (a.orderKey?.from ?? 0) - (b.orderKey?.from ?? 0),
          );
        setState({
          update: (state) =>
            sorted.reduce(
              (s, { itemId, silenceSegments }) =>
                removeSilence({ state: s, itemId, silenceSegments }),
              state,
            ),
          commitToUndoStack: true,
        });
      } catch (err) {
        toast({
          title: t('Editor.timeline.context-menu.remove-silence'),
          description:
            (err as Error).message ??
            t('Editor.timeline.context-menu.remove-silence-error'),
          type: 'error',
          toastOptions: { position: 'top-center' },
        });
      } finally {
        setIsRemovingSilence(false);
      }
    },
    [itemsRemovableSilence, assets, fps, tracks, setState, t],
  );

  const handleOpenRemoveSilenceDialog = useCallback(
    (e: Event) => {
      e.stopPropagation();
      const toProcess = itemsRemovableSilence.filter((it) => {
        const a = getAssetFromItem({ item: it, assets });
        return a?.remoteUrl != null;
      });
      if (toProcess.length === 0) {
        toast({
          title: t('Editor.timeline.context-menu.remove-silence'),
          description: t(
            'Editor.timeline.context-menu.remove-silence-no-audio',
          ),
          type: 'error',
          toastOptions: { position: 'top-center' },
        });
        return;
      }
      onRequestRemoveSilenceDialog?.(handleConfirmRemoveSilence);
    },
    [
      itemsRemovableSilence,
      assets,
      t,
      onRequestRemoveSilenceDialog,
      handleConfirmRemoveSilence,
    ],
  );

  const handleContextMenuPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
    },
    [],
  );

  return (
    <>
      {FEATURE_CUT_LAYERS ? (
        <ContextMenuItem
          onSelect={handleCut}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.cut')}
            subText={`${modKey}X`}
            className="w-full"
          />
        </ContextMenuItem>
      ) : null}
      {FEATURE_COPY_LAYERS && (
        <ContextMenuItem
          onSelect={handleCopy}
          onPointerDown={handleContextMenuPointerDown}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.copy')}
            subText={`${modKey}C`}
            className="w-full"
          />
        </ContextMenuItem>
      )}
      {FEATURE_DUPLICATE_LAYERS && (
        <ContextMenuItem
          onSelect={handleDuplicate}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.duplicate')}
            subText={`${modKey}D`}
            className="w-full"
          />
        </ContextMenuItem>
      )}
      {itemsRemovableSilence.length > 0 ? (
        <ContextMenuItem
          onSelect={handleOpenRemoveSilenceDialog}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked || !hasAnyRemovableWithUrl || isRemovingSilence}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={
              isRemovingSilence
                ? t('Editor.timeline.context-menu.remove-silence-loading')
                : t('Editor.timeline.context-menu.remove-silence')
            }
            className="w-full"
          />
        </ContextMenuItem>
      ) : null}
      {(item.type==='video'||item.type==='image')&&<FrameVideoContextMenu clipId={item.id} sourceType={item.type}/>}
      {item.type==='captions'&&<CaptionStyleContextMenu clipId={item.id}/>}
      {item.type==='image'&&<ImageActionsContextMenu clipId={item.id}/>}
      <ClipAudioContextMenu clipId={item.id} />
      <AudioMergeContextMenu clipId={item.id} />
      <ContextMenuSeparator />
      {FEATURE_BRING_TO_FRONT ? (
        <ContextMenuItem
          onSelect={handleBringToFront}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.bring-to-front')}
            className="w-full"
          />
        </ContextMenuItem>
      ) : null}
      {FEATURE_SEND_TO_BACK ? (
        <ContextMenuItem
          onSelect={handleSendToBack}
          onPointerDown={handleContextMenuPointerDown}
          disabled={isLocked}
        >
          <MenuItem
            size="md"
            showLeadingIcon={false}
            text={t('Editor.timeline.context-menu.send-to-back')}
            className="w-full"
          />
        </ContextMenuItem>
      ) : null}
    </>
  );
};
